import { ScanCardButton } from '@/features/ai/ai-pages';
import { useUserOptions } from '@/hooks/use-user-options';
import { Link } from 'react-router-dom';
import { formatDate } from '@/lib/format';
import { useSession } from '@/features/auth/use-session';
import { BulkMenu, ExportViewButton, FilterButton, ViewTabs, customColumns, useProperties, useRecordList } from './records';
import { Avatar } from '@/components/ui/misc';
import { usePermission } from '@/hooks/use-permission';
import { BulkDelete, BulkExport } from '@/components/shared/bulk';
import { ObjectIndex, FilterPill, AddButton } from '@/components/shared/object-index';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Building2, Star, Users } from 'lucide-react';

import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Switch } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useDebounce } from '@/hooks/use-debounce';
import { ApiRequestError } from '@/lib/api';
import { useCompanyOptions, useContacts, useDeleteContact, useSaveContact } from './api';
import type { Contact } from './types';

export function ContactsPage() {
  const navigate = useNavigate();
  const { can } = usePermission();
  const { data: me } = useSession();
  const [companyId, setCompanyId] = useState('');
  const [editing, setEditing] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);
  const remove = useDeleteContact();
  const list = useRecordList(
    'contact',
    [
      { key: 'all', label: 'All contacts' },
      { key: 'mine', label: 'My contacts', ownerId: 'me' },
      { key: 'unassigned', label: 'Unassigned contacts', ownerId: 'none' },
      { key: 'decision', label: 'Decision makers', filters: [{ field: 'isDecisionMaker', op: 'is_true' }] },
    ],
    me?.id,
  );
  const { data: properties = [] } = useProperties('contact');

  const debounced = useDebounce(list.search);
  const { data: companies = [] } = useCompanyOptions();
  const { data, isLoading, error, refetch } = useContacts({
    ...list.params,
    q: debounced || undefined,
    companyId: companyId || undefined,
  });
  const rows = data?.data ?? [];

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'first_name',
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={row.name} size={30} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-link truncate font-semibold hover:underline">{row.name}</span>
              {row.isPrimary && (
                <span title="Primary contact">
                  <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                </span>
              )}
            </div>
            {row.designation && <div className="truncate text-xs text-muted-foreground">{row.designation}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      sortKey: 'email',
      className: 'text-sm',
      cell: (row) => row.email ?? <span className="text-muted-foreground">--</span>,
    },
    {
      key: 'phone',
      header: 'Phone number',
      className: 'text-sm',
      cell: (row) => (row.phone ? <span dir="ltr">{row.phone}</span> : <span className="text-muted-foreground">--</span>),
    },
    {
      key: 'owner',
      header: 'Contact owner',
      className: 'text-sm',
      cell: (row) => row.owner?.name ?? <span className="text-muted-foreground">No owner</span>,
    },
    {
      key: 'company',
      header: 'Primary company',
      className: 'text-sm',
      cell: (row) =>
        row.company ? (
          <span className="inline-flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            {row.company.name}
          </span>
        ) : (
          <span className="text-muted-foreground">--</span>
        ),
    },
    {
      key: 'flags',
      header: 'Role',
      hidden: true,
      cell: (row) =>
        row.isDecisionMaker ? <Badge variant="default">Decision maker</Badge> : <span className="text-xs text-muted-foreground">--</span>,
    },
    {
      key: 'consent',
      header: 'Consent',
      hidden: true,
      cell: (row) => (
        <div className="flex gap-1">
          {row.emailOptIn && <Badge variant="secondary">Email</Badge>}
          {row.whatsappOptIn && <Badge variant="secondary">WhatsApp</Badge>}
          {!row.emailOptIn && !row.whatsappOptIn && <span className="text-xs text-muted-foreground">None</span>}
        </div>
      ),
    },
    {
      key: 'deals',
      header: 'Deals',
      className: 'tabular text-sm',
      cell: (row) => row.dealCount,
    },
    {
      key: 'created',
      header: 'Create date',
      sortKey: 'created_at',
      hidden: true,
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.createdAt),
    },
    ...customColumns<Contact>(properties),
  ];

  return (
    <div>
      <ObjectIndex
        title="Contacts"
        count={data?.meta?.total}
        viewsNode={<ViewTabs type="contact" list={list} />}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder="Search name, email, phone"
        filters={
          <>
            <FilterPill
              label="Company"
              value={companyId}
              onChange={(value) => {
                setCompanyId(value);
                list.setPage(1);
              }}
              options={companies.map((company) => ({ value: company.id, label: company.name }))}
            />
            <FilterButton type="contact" list={list} />
          </>
        }
        hasFilters={Boolean(companyId || list.extra.conditions.length)}
        onClearFilters={() => {
          setCompanyId('');
          list.setExtra({ match: 'ALL', conditions: [] });
        }}
        toolbarEnd={
          <>
            <Can permission="crm.contact.update">
              <Link
                to="/crm/duplicates?type=contact"
                className="hidden h-8 items-center rounded-md border px-2.5 text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground md:flex"
              >
                Duplicates
              </Link>
            </Can>
            <ExportViewButton type="contact" list={list} />
          </>
        }
        actions={
          <Can permission="crm.contact.create">
            <ScanCardButton />
            <AddButton onClick={() => setCreating(true)} label="Add contact" />
          </Can>
        }
      >
        <DataTable
          bare
          storageKey="contacts"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          error={error ? 'Could not load contacts.' : null}
          onRetry={() => void refetch()}
          meta={data?.meta}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          onRowClick={(row) => navigate(`/crm/contacts/${row.id}`)}
          sort={list.sort}
          onSortChange={list.setSort}
          bulkActions={(ids, clear) => (
            <>
              <BulkMenu type="contact" ids={ids} onDone={clear} />
              <BulkExport
                rows={rows}
                ids={ids}
                rowKey={(row) => row.id}
                filename="contacts"
                fields={[
                  { header: 'Name', value: (row) => row.name },
                  { header: 'Email', value: (row) => row.email },
                  { header: 'Phone', value: (row) => row.phone },
                  { header: 'Company', value: (row) => row.company?.name },
                  { header: 'Job title', value: (row) => row.designation },
                  { header: 'Owner', value: (row) => row.owner?.name },
                ]}
              />
              {can('crm.contact.delete') && (
                <BulkDelete ids={ids} onDone={clear} remove={(id) => remove.mutateAsync(id)} noun="contacts" />
              )}
            </>
          )}
          emptyTitle="No contacts here"
          emptyDescription="Add a person, change the filters, or convert a lead to create one automatically."
          emptyAction={
            <Can permission="crm.contact.create">
              <Button onClick={() => setCreating(true)}>
                <Users className="h-4 w-4" />
                Add contact
              </Button>
            </Can>
          }
        />
      </ObjectIndex>

      {(creating || editing) && (
        <ContactDialog
          contact={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

export function ContactDialog({ contact, onClose }: { contact?: Contact; onClose: () => void }) {
  const save = useSaveContact();
  const { data: companies = [] } = useCompanyOptions();
  const { data: users = [] } = useUserOptions();
  const { data: me } = useSession();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    companyId: contact?.companyId ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    whatsapp: contact?.whatsapp ?? '',
    designation: contact?.designation ?? '',
    ownerId: contact ? (contact.ownerId ?? '') : (me?.id ?? ''),
    department: contact?.department ?? '',
    linkedin: contact?.linkedin ?? '',
    isPrimary: contact?.isPrimary ?? false,
    isDecisionMaker: contact?.isDecisionMaker ?? false,
    emailOptIn: contact?.emailOptIn ?? false,
    whatsappOptIn: contact?.whatsappOptIn ?? false,
    smsOptIn: contact?.smsOptIn ?? false,
    optInSource: contact?.optInSource ?? '',
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ ...(contact ? { id: contact.id } : {}), ...form });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the contact.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{contact ? contact.name : 'New contact'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name">
            <Input
              value={form.firstName}
              autoFocus
              onChange={(e) => set({ firstName: e.target.value })}
            />
          </Field>
          <Field label="Last name">
            <Input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} />
          </Field>
          <Field label="Contact owner" className="sm:col-span-2">
            <Select value={form.ownerId} onChange={(e) => set({ ownerId: e.target.value })}>
              <option value="">No owner</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Company" className="sm:col-span-2">
            <Select value={form.companyId} onChange={(e) => set({ companyId: e.target.value })}>
              <option value="">Not linked</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set({ email: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="WhatsApp">
            <Input value={form.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} />
          </Field>
          <Field label="Job title">
            <Input value={form.designation} onChange={(e) => set({ designation: e.target.value })} />
          </Field>
          <Field label="Department">
            <Input value={form.department} onChange={(e) => set({ department: e.target.value })} />
          </Field>
          <Field label="LinkedIn">
            <Input value={form.linkedin} onChange={(e) => set({ linkedin: e.target.value })} />
          </Field>

          <div className="space-y-2 sm:col-span-2">
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.isPrimary}
                onCheckedChange={(checked) => set({ isPrimary: checked === true })}
              />
              <span className="text-sm">
                Primary contact for this company
                <span className="block text-xs text-muted-foreground">
                  Promoting someone demotes whoever held it before.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.isDecisionMaker}
                onCheckedChange={(checked) => set({ isDecisionMaker: checked === true })}
              />
              <span className="text-sm">Decision maker</span>
            </label>
          </div>

          <div className="space-y-2 rounded-md border p-3 sm:col-span-2">
            <div className="text-sm font-medium">Marketing consent</div>
            <p className="text-xs text-muted-foreground">
              Only tick what this person has actually agreed to. The source and timestamp are
              recorded the first time each is switched on.
            </p>
            <div className="mt-2 space-y-2">
              {(
                [
                  ['emailOptIn', 'Email'],
                  ['whatsappOptIn', 'WhatsApp'],
                  ['smsOptIn', 'SMS'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between">
                  <span className="text-sm">{label}</span>
                  <Switch
                    checked={form[key]}
                    onCheckedChange={(checked) => set({ [key]: checked } as Partial<typeof form>)}
                  />
                </label>
              ))}
            </div>
            {(form.emailOptIn || form.whatsappOptIn || form.smsOptIn) && (
              <div className="mt-2 space-y-1.5">
                <Label className="text-xs">How was consent given?</Label>
                <Input
                  className="h-8"
                  value={form.optInSource}
                  placeholder="Web form, signed contract, verbal at meeting…"
                  onChange={(e) => set({ optInSource: e.target.value })}
                />
              </div>
            )}
            {contact?.optInAt && (
              <p className="text-xs text-muted-foreground">
                Recorded {new Date(contact.optInAt).toLocaleDateString()}
                {contact.optInSource ? ` · ${contact.optInSource}` : ''}
              </p>
            )}
          </div>
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || form.firstName.trim().length === 0}>
            {save.isPending ? 'Saving…' : contact ? 'Save changes' : 'Create contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
